import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import { useSelector } from "react-redux";
import { selectAllOfficeEntities } from '@features/desks/store/advanceHotDeskingSetup.duck';
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import Select from "react-select";
import { REACT_SELECT_DROPDOWN_STYLES } from '@shared/ui/organisms/OfficeDropDownList';
import DateFlicker, { DateScale } from '@shared/ui/molecules/DateFlicker';
import { ToggleableOutlineButton } from '@shared/ui/atoms/buttons/ToggleableOutlineButton';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { getVisitorReport, VisitorReport, VisitorReportLine } from '@features/reporting/services/VisitorReportService';
import { toCsvDownload } from '@features/reporting/services/ReportingService';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { Center } from '@shared/ui/atoms/StructuralLayout';
import { BodyLarge, BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import { HeadlineLarge } from '@shared/ui/atoms/fonts/Headline';
import Colours from '@shared/ui/atoms/Colours';
import { MountedContentCard, MountedContentCardContent } from '@shared/ui/atoms/MountedContentCard';
import moment, { Moment } from "moment";
import { tablet } from '@shared/ui/atoms/MediaQueries';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CustomToolTip } from "../components/CustomToolTip";
import { FlatContentCard } from '@shared/ui/atoms/FlatContentCard';

const COMPANY_WIDE_ID = -1;

type PeriodScale = DateScale.Day | DateScale.Week | DateScale.Month;

function periodBounds(anchor: Moment, scale: PeriodScale): { from: Moment, to: Moment } {
  switch (scale) {
    case DateScale.Day: return { from: anchor.clone().startOf('day'), to: anchor.clone().endOf('day') };
    case DateScale.Week: return { from: anchor.clone().startOf('isoWeek'), to: anchor.clone().endOf('isoWeek') };
    case DateScale.Month:
    default: return { from: anchor.clone().startOf('month'), to: anchor.clone().endOf('month') };
  }
}

export function VisitorReportingPage() {
  const {t} = useTranslation();
  const offices = useSelector(selectAllOfficeEntities);
  const rootOffices = useMemo(() => offices.filter((o: OfficeEntity) => !o.parentId), [offices]);

  const officeOptions = useMemo(() => {
    const companyWide = { value: COMPANY_WIDE_ID, label: t('reports-page.visitors-company-wide') };
    const officeItems = rootOffices.map((o: OfficeEntity) => ({ value: o.id, label: o.label }));
    return [companyWide, ...officeItems];
  }, [rootOffices, t]);

  const [selectedOption, setSelectedOption] = useState<{ value: number; label: string } | null>(null);
  const office = useMemo(() => {
    if (!selectedOption || selectedOption.value === COMPANY_WIDE_ID) return undefined;
    return rootOffices.find((o: OfficeEntity) => o.id === selectedOption.value);
  }, [selectedOption, rootOffices]);

  const [scale, setScale] = useState<PeriodScale>(DateScale.Week);
  const [anchorDate, setAnchorDate] = useState<Moment>(moment());
  const {from: fromDate, to: toDate} = useMemo(() => periodBounds(anchorDate, scale), [anchorDate, scale]);
  const fromKey = fromDate.format('YYYY-MM-DD');
  const toKey = toDate.format('YYYY-MM-DD');

  const [reportData, setReportData] = useState<VisitorReport | undefined>();
  const [loading, setLoading] = useState(false);

  const isCompanyWide = selectedOption?.value === COMPANY_WIDE_ID;

  useEffect(() => {
    if (!selectedOption && officeOptions.length > 0) {
      setSelectedOption(officeOptions[0]);
    }
  }, [officeOptions, selectedOption]);

  useEffect(() => {
    if (!selectedOption) return;
    let cancelled = false;
    setLoading(true);
    setReportData(undefined);
    getVisitorReport(isCompanyWide ? undefined : office, moment(fromKey), moment(toKey))
      .then(data => { if (!cancelled) setReportData(data); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedOption, isCompanyWide, office, fromKey, toKey]);

  const onScaleChange = useCallback((next: PeriodScale) => setScale(next), []);

  const dailyChartData = useMemo(() => {
    if (!reportData?.dailyBreakdown || scale === DateScale.Day) return [];
    return reportData.dailyBreakdown.map(day => ({
      name: moment(day.date).format('DD MMM'),
      visitors: day.visitors,
      checkedIn: day.checkedIn,
      toolTipTitle: moment(day.date).format('ddd DD MMM YYYY'),
    }));
  }, [reportData, scale]);

  const statusLabel = (line: VisitorReportLine) => {
    switch (line.signInState) {
      case 'CHECKED_IN': return t('reports-page.visitors-status-checked-in');
      case 'CHECKED_OUT': return t('reports-page.visitors-status-checked-out');
      default: return t('reports-page.visitors-status-none');
    }
  };

  const formatTimestamp = (value: string | null) => value ? moment(value, 'YYYY-MM-DD HH:mm').format('DD MMM HH:mm') : '-';

  const downloadCsv = () => {
    if (!reportData || reportData.visitors.length === 0) return;
    const rows = reportData.visitors.map(line => ({
      [t('reports-page.visitors-table-date')]: moment(line.date).format('DD/MM/YYYY'),
      [t('reports-page.visitors-table-due')]: line.timeDue,
      [t('reports-page.visitors-table-visitor')]: line.visitorName,
      [t('reports-page.visitors-table-company')]: line.companyName ?? '',
      [t('reports-page.visitors-table-email')]: line.email ?? '',
      [t('reports-page.visitors-table-host')]: line.hostName ?? '',
      [t('reports-page.visitors-table-office')]: line.officeLabel ?? '',
      [t('reports-page.visitors-table-status')]: statusLabel(line),
      [t('reports-page.visitors-table-checked-in')]: line.checkedInAt ?? '',
      [t('reports-page.visitors-table-checked-out')]: line.checkedOutAt ?? '',
    }));
    const officePart = (reportData.officeLabel || 'all-offices').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    toCsvDownload(rows, `visitors-${officePart}-${fromKey}-to-${toKey}`);
  };

  const hasVisitors = !!reportData && reportData.visitors.length > 0;

  return (
    <ReportingContainer>
      <GoBackButton url={"/manage/reporting"} />
      <HeadlineLarge style={{marginBottom: 8}}>{t('reports-page.visitors-report-title')}</HeadlineLarge>
      <BodySmall colour={Colours.darkGrey} style={{marginBottom: 16}}>{t('reports-page.visitors-disclaimer')}</BodySmall>

      <ControlsRow>
        <ControlGroup>
          <BodyRegular weight={600} style={{marginBottom: 8}}>{t('reports-page.visitors-office')}</BodyRegular>
          <Select options={officeOptions}
                  value={selectedOption}
                  onChange={(item: any) => setSelectedOption(item)}
                  styles={REACT_SELECT_DROPDOWN_STYLES}
                  isDisabled={loading}
                  isSearchable={false} />
        </ControlGroup>
        <PeriodGroup>
          <BodyRegular weight={600} style={{marginBottom: 8}}>{t('reports-page.visitors-period')}</BodyRegular>
          <ScaleRow>
            <ToggleableOutlineButton isActive={scale === DateScale.Day} click={() => onScaleChange(DateScale.Day)} text={'button.day-view'} disabled={loading} />
            <ToggleableOutlineButton isActive={scale === DateScale.Week} click={() => onScaleChange(DateScale.Week)} text={'button.week-view'} disabled={loading} />
            <ToggleableOutlineButton isActive={scale === DateScale.Month} click={() => onScaleChange(DateScale.Month)} text={'button.month-view'} disabled={loading} />
          </ScaleRow>
          <DateFlicker dateScale={scale}
                       updateGlobalDate={false}
                       date={anchorDate}
                       onChange={(date: Moment) => setAnchorDate(date)}
                       showDateRange={scale !== DateScale.Day}
                       disabled={loading} />
        </PeriodGroup>
      </ControlsRow>

      {loading && (
        <Center>
          <LoadingSpinner hideBorder={true} />
        </Center>
      )}

      {!loading && reportData && !hasVisitors && (
        <Center style={{marginTop: 32}}>
          <MountedContentCard>
            <MountedContentCardContent>
              <BodyLarge weight={600} colour={Colours.blue} style={{marginBottom: 12}}>{t('reports-page.visitors-no-data')}</BodyLarge>
              <BodyRegular>{t('reports-page.visitors-no-data-description')}</BodyRegular>
            </MountedContentCardContent>
          </MountedContentCard>
        </Center>
      )}

      {!loading && reportData && hasVisitors && (
        <>
          <SummaryCard>
            <SummaryTitle>{reportData.officeLabel}: {fromDate.format('D MMM YYYY')}{scale !== DateScale.Day && ` to ${toDate.format('D MMM YYYY')}`}</SummaryTitle>
            <SummaryGrid>
              <SummaryItem>
                <SummaryValue>{reportData.totalVisitors}</SummaryValue>
                <SummaryLabel>{t('reports-page.visitors-total')}</SummaryLabel>
              </SummaryItem>
              <SummaryItem>
                <SummaryValue>{reportData.checkedIn}</SummaryValue>
                <SummaryLabel>{t('reports-page.visitors-checked-in')}</SummaryLabel>
              </SummaryItem>
              <SummaryItem>
                <SummaryValue>{reportData.checkedOut}</SummaryValue>
                <SummaryLabel>{t('reports-page.visitors-checked-out')}</SummaryLabel>
              </SummaryItem>
              <SummaryItem>
                <SummaryValue>{reportData.notCheckedIn}</SummaryValue>
                <SummaryLabel>{t('reports-page.visitors-not-checked-in')}</SummaryLabel>
              </SummaryItem>
            </SummaryGrid>
          </SummaryCard>

          <FlatContentCard style={{marginBottom: 24}}>
            <BodyLarge weight={600} style={{marginBottom: 4}}>{t('reports-page.visitors-download-title')}</BodyLarge>
            <BodyRegular colour={Colours.darkGrey} style={{marginBottom: 12}}>{t('reports-page.visitors-download-description')}</BodyRegular>
            <OutlineButton text="button.download-csv"
                           disabled={loading || !hasVisitors}
                           click={downloadCsv} />
          </FlatContentCard>

          {dailyChartData.length > 0 && (
            <FlatContentCard style={{marginBottom: 24}}>
              <BodyLarge weight={600} style={{marginBottom: 12}}>{t('reports-page.visitors-daily-chart')}</BodyLarge>
              <div style={{height: 300}}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dailyChartData}
                             margin={{top: 20, right: 20, left: 0, bottom: 5}}>
                    <CartesianGrid stroke={Colours.lightGrey} strokeDasharray="5 5" />
                    <XAxis dataKey="name"
                           fontFamily={'"soleil", sans-serif'}
                           fill={Colours.darkGrey}
                           fontSize={12}
                           axisLine={false}
                           tickLine={false} />
                    <YAxis fontFamily={'"soleil", sans-serif'}
                           fontSize={12}
                           axisLine={false}
                           tickLine={false}
                           allowDecimals={false} />
                    <Tooltip content={<CustomToolTip />} />
                    <Line type="monotone"
                          dataKey="visitors"
                          stroke={Colours.blue}
                          strokeWidth={2}
                          dot={{fill: Colours.blue, r: 3}}
                          activeDot={{r: 5}} />
                    <Line type="monotone"
                          dataKey="checkedIn"
                          stroke={Colours.green}
                          strokeWidth={2}
                          dot={{fill: Colours.green, r: 3}}
                          activeDot={{r: 5}} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </FlatContentCard>
          )}

          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th>{t('reports-page.visitors-table-date')}</Th>
                  <Th>{t('reports-page.visitors-table-due')}</Th>
                  <Th>{t('reports-page.visitors-table-visitor')}</Th>
                  <Th>{t('reports-page.visitors-table-company')}</Th>
                  <Th>{t('reports-page.visitors-table-host')}</Th>
                  <Th>{t('reports-page.visitors-table-office')}</Th>
                  <Th>{t('reports-page.visitors-table-status')}</Th>
                  <Th>{t('reports-page.visitors-table-checked-in')}</Th>
                  <Th>{t('reports-page.visitors-table-checked-out')}</Th>
                </tr>
              </thead>
              <tbody>
                {reportData.visitors.map((line: VisitorReportLine) => (
                  <VisitorRow key={line.bookingId}>
                    <Td>{moment(line.date).format('ddd D MMM')}</Td>
                    <Td>{line.timeDue}</Td>
                    <Td style={{fontWeight: 600}}>
                      {line.visitorName}
                      {line.email && <EmailLine>{line.email}</EmailLine>}
                    </Td>
                    <Td>{line.companyName ?? '-'}</Td>
                    <Td>{line.hostName ?? '-'}</Td>
                    <Td>{line.officeLabel ?? '-'}</Td>
                    <Td>{statusLabel(line)}</Td>
                    <Td>{formatTimestamp(line.checkedInAt)}</Td>
                    <Td>{formatTimestamp(line.checkedOutAt)}</Td>
                  </VisitorRow>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        </>
      )}
    </ReportingContainer>
  );
}

const ReportingContainer = styled.div`
  max-width: 100%;
  min-height: 100vh;
  background-color: white;
  padding: 24px;
`

const ControlsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
  @media (${tablet}) {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
  }
`

const ControlGroup = styled.div`
  width: 100%;
  @media (${tablet}) {
    max-width: 320px;
  }
`

const PeriodGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  @media (${tablet}) {
    max-width: 520px;
    margin-left: auto;
    align-items: flex-end;
    text-align: right;
  }
`

const ScaleRow = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 12px;
  @media (${tablet}) {
    justify-content: flex-end;
  }
`

const SummaryCard = styled.div`
  background-color: ${Colours.blue10};
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
`

const SummaryTitle = styled.h3`
  font-family: "soleil", sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: ${Colours.black};
  margin: 0 0 16px 0;
`

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  @media (${tablet}) {
    grid-template-columns: repeat(4, 1fr);
  }
`

const SummaryItem = styled.div`
  text-align: center;
`

const SummaryValue = styled.div`
  font-family: "soleil", sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: ${Colours.blue};
`

const SummaryLabel = styled.div`
  font-family: "soleil", sans-serif;
  font-size: 13px;
  color: ${Colours.darkGrey};
  margin-top: 4px;
`

const TableWrapper = styled.div`
  margin-top: 8px;
  overflow-x: auto;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`

const Th = styled.th`
  font-family: "soleil", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${Colours.darkGrey};
  text-align: left;
  padding: 8px 12px;
  border-bottom: 2px solid ${Colours.mildGrey};
  white-space: nowrap;
`

const Td = styled.td`
  font-family: "soleil", sans-serif;
  font-size: 14px;
  color: ${Colours.black};
  padding: 10px 12px;
  border-bottom: 1px solid ${Colours.mildGrey};
  vertical-align: top;
`

const EmailLine = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: ${Colours.darkGrey};
`

const VisitorRow = styled.tr`
  &:hover {
    background-color: ${Colours.blue10};
  }
`
